import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map, Observable, of} from 'rxjs';
import {environment} from '../../../../environments/environment';

export interface ShineDomainRecord {
    id: string;
    clientId: string;
    domain: string;
    status: string;
    created_at?: string;
    updated_at?: string;
    verified_at?: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class ShineAuthDomainsService {
    private http = inject(HttpClient);
    private readonly baseUrl = (environment.authBaseServerUrl || 'https://auth.shine.intuvio.com').replace(/\/+$/, '');

    listVerifiedDomains(clientId: string): Observable<ShineDomainRecord[]> {
        if (!clientId) return of([]);

        const params = new HttpParams().set('client_id', clientId).set('status', 'verified');
        return this.http.get<ShineDomainRecord[]>(`${this.baseUrl}/api/domains`, {params}).pipe(
            map(response => Array.isArray(response) ? response : []),
            catchError(error => {
                console.error('Error loading verified domains from Shine Auth:', error);
                return of([]);
            })
        );
    }

    listDomains(clientId: string): Observable<ShineDomainRecord[]> {
        if (!clientId) return of([]);

        const params = new HttpParams().set('client_id', clientId).set('status', 'all');
        return this.http.get<ShineDomainRecord[]>(`${this.baseUrl}/api/domains`, {params}).pipe(
            map(response => Array.isArray(response) ? response : []),
            catchError(error => {
                console.error('Error loading domains from Shine Auth:', error);
                return of([]);
            })
        );
    }

    createDomain(
        clientId: string,
        payload: {domain: string; status?: string; metadata?: Record<string, unknown> | null}
    ): Observable<ShineDomainRecord | null> {
        if (!clientId) return of(null);
        const params = new HttpParams().set('client_id', clientId);
        return this.http.post<ShineDomainRecord>(`${this.baseUrl}/api/domains`, payload, {params}).pipe(
            map(response => response ?? null),
            catchError(error => {
                console.error('Error creating domain in Shine Auth:', error);
                return of(null);
            })
        );
    }

    updateDomain(
        clientId: string,
        id: string,
        payload: {domain?: string; status?: string; metadata?: Record<string, unknown> | null}
    ): Observable<ShineDomainRecord | null> {
        if (!clientId || !id) return of(null);
        const params = new HttpParams().set('client_id', clientId);
        return this.http.put<ShineDomainRecord>(`${this.baseUrl}/api/domains/${id}`, payload, {params}).pipe(
            map(response => response ?? null),
            catchError(error => {
                console.error('Error updating domain in Shine Auth:', error);
                return of(null);
            })
        );
    }

    deleteDomain(clientId: string, id: string): Observable<boolean> {
        if (!clientId || !id) return of(false);
        const params = new HttpParams().set('client_id', clientId);
        return this.http.delete<void>(`${this.baseUrl}/api/domains/${id}`, {params}).pipe(
            map(() => true),
            catchError(error => {
                console.error('Error deleting domain in Shine Auth:', error);
                return of(false);
            })
        );
    }
}
